import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Skeleton } from '@chakra-ui/react';
import { openNewTab } from '../../../utils/UIUtils';
import PhotoCardDetailBottomSheet from './PhotoCardDetailBottomSheet';
import type { PhotoCardType } from '../../types/PhotoCardType';

export default function NewPhotoCard({
  photoCard,
  photoCardSize,
  ...props
}: {
  photoCard: PhotoCardType;
  photoCardSize: 'xs' | 'sm' | 'md' | 'lg';
}) {
  const { i18n } = useTranslation();
  const isEnLocale = i18n.language?.startsWith('en') ?? false;
  const targetRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isShowPhotoCardBottomSheet, setIsShowPhotoCardBottomSheet] = useState(false);

  const getPhotoCardHeight = () => {
    switch (photoCardSize) {
      case 'xs':
        return 62;
      case 'sm':
        return 90;
      case 'md':
        return 117;
      case 'lg':
        return 182;
      default:
        return 289;
    }
  };

  const getPhotoCardWidth = () => {
    switch (photoCardSize) {
      case 'xs':
        return 62;
      case 'sm':
        return 57;
      case 'md':
        return 117;
      case 'lg':
        return 182;
      default:
        return 289;
    }
  };

  const isPhotoCardCollected = photoCard.collectedCount > 0;

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsVisible(entry.isIntersecting);
      });
    });

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);

  const onClickPhotoCard = () => {
    if (isEnLocale) {
      setIsShowPhotoCardBottomSheet(true);
      return;
    }
    openNewTab(`/photo_cards/${photoCard.photoCardId}`);
  };

  return (
    <div
      key={photoCard.photoCardId}
      ref={targetRef}
      style={{
        width: getPhotoCardWidth(),
        height: getPhotoCardHeight(),
        flexShrink: 0 // 중요: 플렉스 컨테이너에서 줄어들지 않게
      }}
      {...props}
    >
      {isVisible ? (
        <Image
          key={photoCard.photoCardId}
          src={photoCard.mediumThumbnailUrl || photoCard.pictureUrl}
          borderRadius="8px"
          onClick={onClickPhotoCard}
          loading={'lazy'}
          height={getPhotoCardHeight()}
          width={getPhotoCardWidth()}
          objectFit="cover"
          filter={isPhotoCardCollected ? 'grayscale(0)' : 'grayscale(1) opacity(0.5)'}
        />
      ) : (
        <Skeleton
          height={getPhotoCardHeight()}
          width={getPhotoCardWidth()} // 스켈레톤에도 width 추가
          startColor="rgba(242,242,242)"
          endColor="rgba(242,242,242,0.7)"
          borderRadius="8px"
        />
      )}
      {isShowPhotoCardBottomSheet && (
        <PhotoCardDetailBottomSheet
          isOpen={isShowPhotoCardBottomSheet}
          onClose={() => setIsShowPhotoCardBottomSheet(false)}
          photoCardId={photoCard.photoCardId}
        />
      )}
    </div>
  );
}